/**
 * PURPOSE: Editar entrenamientos públicos de forma ordenada y práctica en Winscore.
 * RESPONSIBILITIES: Filtrar/agrupar por fecha, insertar debajo, sincronizar institución y completar sedes vacías por fecha.
 * COLLABORATORS: CardConfigSportInformation.jsx, SearchableVenueSelect.jsx y trainingEditor.utils.js.
 * ROUTE: winscore/src/components/Cards/CardConfiguration/CardConfigSportInformation/TrainingEditor.jsx
 */
import React, { useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import { FaMapMarkerAlt, FaPlus, FaTrash } from "react-icons/fa";

import { SearchableVenueSelect } from "./SearchableVenueSelect.jsx";
import {
  ALL_TRAINING_DATES,
  UNDEFINED_TRAINING_DATE,
  createTraining,
  formatTrainingDateHeading,
  formatTrainingDateLabel,
  getTrainingDateKey,
  sortTrainingDateKeys,
} from "./trainingEditor.utils.js";

function TrainingRow({
  training,
  onUpdate,
  onRemove,
  onAddBelow,
  institutionOptions,
  venueOptions,
}) {
  const handleInstitutionChange = (institutionId) => {
    const selected = institutionOptions.find(
      (institution) => String(institution.id) === String(institutionId),
    );

    onUpdate({
      institutionId: selected ? String(selected.id) : "",
      institutionName: selected?.name || "",
    });
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 shadow-sm">
      <div className="grid gap-3 lg:grid-cols-[130px_110px_110px_minmax(160px,1fr)_minmax(190px,1fr)_44px] lg:items-end">
        <label className="grid gap-1 text-xs font-semibold text-gray-600">
          Fecha
          <input
            type="date"
            value={training.date}
            onChange={(event) => onUpdate({ date: event.target.value })}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
          />
        </label>
        <label className="grid gap-1 text-xs font-semibold text-gray-600">
          Hora inicio
          <input
            type="text"
            placeholder="8:00am"
            value={training.startTime}
            onChange={(event) => onUpdate({ startTime: event.target.value })}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
          />
        </label>
        <label className="grid gap-1 text-xs font-semibold text-gray-600">
          Hora fin
          <input
            type="text"
            placeholder="10:00am"
            value={training.endTime}
            onChange={(event) => onUpdate({ endTime: event.target.value })}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
          />
        </label>
        <label className="grid gap-1 text-xs font-semibold text-gray-600">
          Institución
          <select
            value={training.institutionId}
            onChange={(event) => handleInstitutionChange(event.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
          >
            <option value="">Sin institución del catálogo</option>
            {institutionOptions.map((institution) => (
              <option key={institution.id} value={institution.id}>
                {institution.name}
              </option>
            ))}
          </select>
        </label>
        <SearchableVenueSelect
          label="Sede"
          value={training.venueId}
          onChange={(value) => onUpdate({ venueId: value })}
          options={venueOptions}
          placeholder="Buscar sede"
        />
        <button
          type="button"
          onClick={onRemove}
          className="flex h-10 w-10 items-center justify-center rounded-lg bg-red-100 text-red-700 transition hover:bg-red-200"
          aria-label="Eliminar entrenamiento"
        >
          <FaTrash />
        </button>
      </div>

      <div className="mt-3 grid gap-3 lg:grid-cols-2">
        <label className="grid gap-1 text-xs font-semibold text-gray-600">
          Nombre manual de institución
          <input
            type="text"
            placeholder="Úsalo solo si la institución no está en la lista"
            value={training.institutionName}
            disabled={Boolean(training.institutionId)}
            onChange={(event) => onUpdate({ institutionName: event.target.value })}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500"
          />
        </label>
        <label className="grid gap-1 text-xs font-semibold text-gray-600">
          Notas
          <input
            type="text"
            placeholder="Indicaciones opcionales"
            value={training.notes}
            onChange={(event) => onUpdate({ notes: event.target.value })}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
          />
        </label>
      </div>

      <div className="mt-3 flex justify-end">
        <button
          type="button"
          onClick={onAddBelow}
          className="inline-flex items-center gap-2 rounded-lg border border-blue-200 bg-white px-3 py-2 text-xs font-semibold text-blue-700 transition hover:bg-blue-50"
        >
          <FaPlus /> Agregar debajo
        </button>
      </div>
    </div>
  );
}

export function TrainingEditor({
  trainings,
  onChange,
  institutionOptions,
  venueOptions,
}) {
  const [activeDate, setActiveDate] = useState(ALL_TRAINING_DATES);
  const [bulkVenueId, setBulkVenueId] = useState("");

  const dateKeys = useMemo(() => {
    const keys = new Set(trainings.map(getTrainingDateKey));
    if (activeDate !== ALL_TRAINING_DATES) keys.add(activeDate);
    return Array.from(keys).sort(sortTrainingDateKeys);
  }, [activeDate, trainings]);

  const groupedTrainings = useMemo(() => {
    const groups = new Map();
    trainings.forEach((training) => {
      const key = getTrainingDateKey(training);
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(training);
    });
    return groups;
  }, [trainings]);

  const visibleGroups = useMemo(() => {
    if (activeDate === ALL_TRAINING_DATES) {
      return dateKeys.map((dateKey) => ({
        dateKey,
        items: groupedTrainings.get(dateKey) || [],
      }));
    }

    return [
      {
        dateKey: activeDate,
        items: groupedTrainings.get(activeDate) || [],
      },
    ];
  }, [activeDate, dateKeys, groupedTrainings]);

  const addTraining = () => {
    const inheritedDate =
      activeDate !== ALL_TRAINING_DATES && activeDate !== UNDEFINED_TRAINING_DATE
        ? activeDate
        : "";
    onChange([...trainings, createTraining(inheritedDate)]);
  };

  const addBelow = (clientId) => {
    const index = trainings.findIndex((training) => training.clientId === clientId);
    if (index < 0) return;
    const current = trainings[index];
    const next = [...trainings];
    next.splice(index + 1, 0, createTraining(current.date || ""));
    onChange(next);
  };

  const updateTraining = (clientId, patch) => {
    onChange(
      trainings.map((training) =>
        training.clientId === clientId ? { ...training, ...patch } : training,
      ),
    );
  };

  const removeTraining = (clientId) => {
    if (trainings.length === 1) {
      onChange([createTraining(activeDate === ALL_TRAINING_DATES ? "" : activeDate === UNDEFINED_TRAINING_DATE ? "" : activeDate)]);
      return;
    }
    onChange(trainings.filter((training) => training.clientId !== clientId));
  };

  const completeVenues = () => {
    if (!bulkVenueId || activeDate === ALL_TRAINING_DATES || activeDate === UNDEFINED_TRAINING_DATE) return;

    let updated = 0;
    const next = trainings.map((training) => {
      if (training.date !== activeDate || training.venueId) return training;
      updated += 1;
      return { ...training, venueId: bulkVenueId };
    });

    if (!updated) {
      toast("Todos los entrenamientos de esta fecha ya tienen sede.");
      return;
    }

    onChange(next);
    toast.success(`${updated} entrenamiento${updated === 1 ? "" : "s"} actualizado${updated === 1 ? "" : "s"}.`);
  };

  const selectedDateForBulk =
    activeDate !== ALL_TRAINING_DATES && activeDate !== UNDEFINED_TRAINING_DATE;

  return (
    <div className="rounded-xl border border-gray-200 p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="text-sm font-bold uppercase tracking-wide text-gray-700">
            Entrenamientos
          </h3>
          <p className="mt-1 text-xs text-gray-500">
            Organiza por fecha, agrega registros en cualquier posición y completa sedes pendientes.
          </p>
        </div>
        <button
          type="button"
          onClick={addTraining}
          className="inline-flex items-center justify-center gap-2 rounded-lg bg-gray-200 px-3 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-300"
        >
          <FaPlus /> Agregar entrenamiento
        </button>
      </div>

      <div className="mt-4 flex gap-2 overflow-x-auto pb-1">
        <button
          type="button"
          onClick={() => setActiveDate(ALL_TRAINING_DATES)}
          className={`shrink-0 rounded-full px-3 py-2 text-xs font-semibold transition ${
            activeDate === ALL_TRAINING_DATES
              ? "bg-blue-600 text-white"
              : "bg-gray-100 text-gray-600 hover:bg-gray-200"
          }`}
        >
          Todas
        </button>
        {dateKeys.map((dateKey) => (
          <button
            type="button"
            key={dateKey}
            onClick={() => {
              setActiveDate(dateKey);
              setBulkVenueId("");
            }}
            className={`shrink-0 rounded-full px-3 py-2 text-xs font-semibold transition ${
              activeDate === dateKey
                ? "bg-blue-600 text-white"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            {dateKey === UNDEFINED_TRAINING_DATE
              ? "Por definir"
              : formatTrainingDateLabel(dateKey)}
          </button>
        ))}
      </div>

      {selectedDateForBulk ? (
        <div className="mt-4 grid gap-3 rounded-xl border border-blue-100 bg-blue-50 p-3 md:grid-cols-[minmax(0,1fr),auto] md:items-end">
          <SearchableVenueSelect
            label={`Sede para ${formatTrainingDateHeading(activeDate)}`}
            value={bulkVenueId}
            onChange={setBulkVenueId}
            options={venueOptions}
            placeholder="Buscar sede para completar"
          />
          <button
            type="button"
            onClick={completeVenues}
            disabled={!bulkVenueId}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <FaMapMarkerAlt /> Completar sedes
          </button>
        </div>
      ) : null}

      <div className="mt-4 grid gap-5">
        {visibleGroups.map((group) => (
          <section key={group.dateKey} className="grid gap-3">
            {activeDate === ALL_TRAINING_DATES ? (
              <div className="flex items-center gap-3">
                <span className="h-px flex-1 bg-gray-200" />
                <h4 className="text-xs font-bold uppercase tracking-[0.12em] text-gray-500">
                  {group.dateKey === UNDEFINED_TRAINING_DATE
                    ? "Por definir"
                    : formatTrainingDateHeading(group.dateKey)}
                </h4>
                <span className="h-px flex-1 bg-gray-200" />
              </div>
            ) : null}

            {group.items.length ? (
              group.items.map((training) => (
                <TrainingRow
                  key={training.clientId}
                  training={training}
                  onUpdate={(patch) => updateTraining(training.clientId, patch)}
                  onRemove={() => removeTraining(training.clientId)}
                  onAddBelow={() => addBelow(training.clientId)}
                  institutionOptions={institutionOptions}
                  venueOptions={venueOptions}
                />
              ))
            ) : (
              <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-5 text-center text-sm font-medium text-gray-500">
                No hay entrenamientos en esta fecha. Usa “Agregar entrenamiento” para crear uno.
              </div>
            )}
          </section>
        ))}
      </div>
    </div>
  );
}
